import { endpoints } from '@/config';
import { Logger } from '@/helpers/log';
import { ACCOUNT_KEY } from '@/store/modules/role';
import { apolloSdk } from '../apollo';
import api from './api';
import { createFilter, getLast as lastFilters } from './filters';
import { createKpi, getLast as lastKpis } from './kpis';
import { fromLegacy, toLegacy } from './migration/dashboards';
import { fromLegacy as fromLegacyFilter } from './migration/filters';
import { syncLegacy, syncRefs } from './migration/sync';
import { fromLegacy as fromLegacyWidget, toLegacy as toLegacyWidget } from './migration/widgets';
import { propertiesToListableProperties } from './useListableProperties';

const logger = new Logger('api:legacy:dashboards');

// legacy db
let references = [];
let allWidgets = [];
let firstRun = true;

export function unTrip() {
  logger.warn('first run trip switch manually reset');
  firstRun = true;
}
export function populate(ref) {
  logger.warn('references override triggered');
  references = [ref];
}

function replaceOrCreateIn(item, array) {
  const index = array.findIndex(({ id }) => id === item.id);
  if (index >= 0) {
    array.splice(index, 1, item);
  } else {
    array.push(item);
  }
  return array;
}
function updateAndTransform(data) {
  const { reference, widgets, dashboard } = fromLegacy(data);
  replaceOrCreateIn(reference, references);
  widgets.forEach((widget) => replaceOrCreateIn(widget, widgets));
  return dashboard;
}

export function updateDashboard(dashboard, force = false, preventLegacy = false) {
  const requested = preventLegacy
    ? dashboard
    : toLegacy(dashboard, allWidgets);

  const endpoint = `${endpoints.dashboards}/${requested.id}`;
  if (force) {
    logger.warn('forcing dashboard update without merge safety', dashboard);
    return api.put(endpoint, requested)
      .then((response) => updateAndTransform(response.data));
  }
  const expected = references.find(({ id }) => id === requested.id);
  if (!expected) {
    logger.error('Cannot find original reference to dashboard', dashboard, references);
    return Promise.reject(new Error('Invalid dashboard id'));
  }
  const data = { expected, requested };

  return api.patch(endpoint, data)
    .then((response) => updateAndTransform(response.data.current))
    .catch((error) => {
      if (!error || !error.response || !error.response.data) {
        return Promise.reject(error);
      }
      const { response: { status, data: body } } = error;
      if (
        status === 400
        && body.errors
        && body.errors.length
        && body.errors[0].includes('includes empty values')
      ) {
        logger.info('failed due to empty values, attempting put');
        return updateDashboard(dashboard, true);
      }
      if (body.current) {
        const { current } = updateAndTransform(body.current);
        const patched = {
          response: {
            data: {
              current,
              ...body,
            },
          },
        };
        return Promise.reject(patched);
      }
      return Promise.reject(error);
    });
}
export function updateWidget(data) {
  const { widget } = fromLegacyWidget(toLegacyWidget(data));
  replaceOrCreateIn(widget, allWidgets);
  // widgets are currently in dashboard object, so update reference.
  const { dashboard: did } = widget;
  const reference = references.find(({ id }) => id === did);
  if (!reference) {
    logger.error('Cannot find original reference to widget dashboard', did, references);
    return Promise.reject(new Error('Invalid dashboard id'));
  }
  const { dashboard } = fromLegacy(reference);
  return updateDashboard(dashboard);
}

const forEachAsync = (iter, fn) => {
  const [i, ...rest] = iter;
  if (i === undefined) {
    return Promise.resolve();
  }
  return fn(i)
    .then(() => forEachAsync(rest, fn))
    .catch((error) => {
      logger.error('for each async swallowed', error);
      return forEachAsync(rest, fn);
    });
};
async function sync(hints, dashboards, accountId) {
  firstRun = false;
  if (hints.length === 0) {
    logger.info('no repair required (no hints)');
    return false;
  }
  const properties = propertiesToListableProperties(await apolloSdk.Properties({ accountId }));
  const filters = lastFilters();
  const kpis = lastKpis();
  if (!properties || !filters || !kpis) {
    logger.error('dashboard cannot sync prior resource fetch; properties, filters, kpis', properties, filters, kpis);
    firstRun = true;
    return false;
  }
  const actual = { properties, filters, kpis };
  const {
    showProperties,
    createFilters,
    createKpis,
    linkProperties,
    linkFilters,
    dirty,
  } = syncLegacy(hints, actual);
  if (!dirty) {
    logger.info('no repair required (none dirty)');
    return false;
  }
  await forEachAsync(Object.values(showProperties), async (property) => {
    // TODO: restore show on hidden functionality.
    logger.warn('skipped repair of hidden but used property', property.name);
  });
  await forEachAsync(Object.entries(createFilters), async ([old, filter]) => {
    const data = fromLegacyFilter(filter);
    const { id } = await createFilter(data);
    linkFilters[old] = id;
    logger.info('resurrected shadow filter', data);
  });
  const linkKpis = {};
  await forEachAsync(Object.entries(createKpis), async ([old, data]) => {
    const { $id } = await createKpi(data);
    linkKpis[old] = $id;
    logger.info('resurrected shadow kpi', data);
  });
  const links = { ...linkProperties, ...linkFilters, ...linkKpis };
  logger.debug('actioning links', links);
  const { changed, updated } = syncRefs(allWidgets, links);
  allWidgets = updated;
  const ids = {};
  changed.forEach((w) => {
    ids[w.dashboard] = dashboards.find(({ id }) => id === w.dashboard);
  });
  await forEachAsync(Object.values(ids), async (data) => {
    await updateDashboard(data, true);
    logger.info('calling update for dashboard with relinked widgets', data);
  });
  return true;
}

export function getDashboards() {
  const accountId = window.localStorage.getItem(ACCOUNT_KEY);
  const endpoint = endpoints.dashboards;
  return api
    .get(endpoint)
    .then(async (response) => {
      const { data: { items } } = response;
      const transformed = items.map(fromLegacy);
      references = transformed.map(({ reference }) => reference);
      allWidgets = [].concat(...transformed.map(({ widgets }) => widgets));
      const dashboards = transformed.map(({ dashboard }) => dashboard);
      let restart = false;
      if (firstRun) {
        logger.info('index first run triggering sync');
        const hints = transformed.map(({ resources }) => resources);
        restart = await sync(hints, dashboards, accountId);
      }
      return { restart, dashboards };
    });
}
export function getWidgets() {
  if (allWidgets.length === 0) {
    logger.warn('getWidgets called before getDashboards');
  }
  return Promise.resolve(JSON.parse(JSON.stringify(allWidgets)));
}

export function createDashboard(dashboard) {
  const endpoint = endpoints.dashboards;
  const created = toLegacy(dashboard, allWidgets);
  return api.post(endpoint, created)
    .then((response) => updateAndTransform(response.data))
    .catch((err) => Promise.reject(err));
}
export function createWidget(widget) {
  replaceOrCreateIn(widget, allWidgets);
  // create widget is done before adding to dashboard
  return Promise.resolve();
}

export function deleteDashboard(dashboard) {
  const { id } = dashboard;
  if (!id) {
    throw new Error('Cannot delete dashboard without id.');
  }
  const endpoint = `${endpoints.dashboards}/${id}`;
  return api.delete(endpoint);
}
export function deleteWidget(/* widget */) {
  // don't bother updating backing db.
  return Promise.resolve();
}
